import { Badge } from '@/components/ui/badge';
import { NetworkError } from '@/errors';
import { downloadInvoicePdf } from '@/services/invoices';
import { displayCurrency, displayDate } from '@/utils/format.utils';
import { getInvoiceStatusVariant } from '@/utils/invoice.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import type { ColumnDef } from '@tanstack/vue-table';
import { FileDown } from 'lucide-vue-next';
import { h } from 'vue';
import { RouterLink } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import type { ModelInvoice } from '@layup/api-sdk';

const handleActionClick = async (id: string, reference: string) => {
  try {
    const url = await downloadInvoicePdf(id);

    if (!url.redirectUrl) {
      const toast = useToast();
      toast.error('No download url found. Please contact support.', { position: 'top' });
      return;
    }

    const link = document.createElement('a');
    link.href = url.redirectUrl;
    link.target = '_blank';
    link.setAttribute('download', `${id}-${reference}-invoice-report.pdf`);
    link.click();

    addMetric('invoices-table-pdf-download', { status: 'success', channel: 'merchant' });
  } catch (error: any) {
    const toast = useToast();
    toast.error(error.response?.data ?? 'Something went wrong', { position: 'top' });

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoices-table-pdf-download', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('invoices-table-pdf-download', { status: 'error', channel: 'merchant' });
    }
  }
};

export const invoicesTableColumns: ColumnDef<ModelInvoice>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const name = displayDate(getValue() as string);
      return h('div', name);
    },
  },
  {
    accessorKey: 'accountingInvoiceNumber',
    header: 'Invoice Number',
    enableSorting: false,
    cell: ({ getValue }) => {
      const invoiceNo = (getValue() as string) || 'Not Available';
      return h('div', invoiceNo);
    },
  },
  {
    accessorKey: 'reference',
    header: 'Reference',
    enableSorting: false,
    cell: ({ getValue, row }) => {
      const ref = getValue() as string;
      const id: string = row.original._id ?? '';
      return h(RouterLink, { to: `/merchant/invoices/${id}`, class: 'text-brand-700 underline p-0 m-0' }, () => ref);
    },
  },
  {
    accessorKey: 'totalAmount',
    header: () => {
      return h('div', { class: 'text-right' }, 'Total Amount');
    },
    enableSorting: false,
    cell: ({ getValue }) => {
      const totalAmount = getValue() as number;
      return h('div', { class: 'text-right' }, displayCurrency(totalAmount));
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      const variant = getInvoiceStatusVariant(status);
      return h(Badge, { class: 'text-xs', variant }, () => status);
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const id: string = row.original._id ?? '';
      const reference: string = row.original.reference ?? '';

      return h(
        'div',
        {
          class: 'cursor-pointer rounded-md hover:bg-gray-200 flex justify-center',
          onClick: () => handleActionClick(id, reference),
        },
        h(FileDown, { class: 'h-4 w-4 text-gray-600 ' }),
      );
    },
  },
];
